// Capability primitives for role-based access control.
//
// Wave 18 introduced the Capability/Role vocabulary. Phase 1 of the
// persona/permissions rework (this file) formalizes approval + payment
// capabilities so every UI check can go through `useCapabilities().can()`
// instead of raw `activeRole === "admin"` checks.
//
// Naming convention:
//   <verb>:<object>_<scope>
// where scope is one of:
//   - omitted        → applies to any row in the org
//   - _own           → author_id === auth.uid()
//   - _any           → any row in the org (alias for omitted scope)
//   - _self          → approving your own submission (opt-in capability)
//   - _high_value    → approving a row above the org's policy threshold
//
// Self-approval semantics (approve:{expenses,invoices}_self):
//   Only owners get it by default. Admins can be granted it per-member via
//   the org_member_capabilities override table (wave 19). When approval
//   code lands in phase 3, it picks `_self` for the author-matches-caller
//   case and the base `approve:...` cap otherwise.
//
// High-value semantics (approve:{expenses,invoices}_high_value):
//   Declared here so the schema + role bundles carry the shape. The runtime
//   threshold check lands with the policy engine in phase 3. Treat as
//   placeholder until then.
//
// Payment semantics (initiate:payments):
//   Moves approved items into the scheduled-payment queue. Phase 4 wires a
//   manual "mark paid" button; phase 5 swaps that for a Plaid Link flow.

export type Capability =
  // View scope
  | "view:mine"
  | "view:company"
  // Write
  | "write:expenses_own"
  | "write:expenses_any"
  | "write:invoices_own"
  | "write:invoices_any"
  | "write:vendors"
  | "write:tax_policies"
  // Read
  | "read:tax_policies"
  | "read:reports"
  | "read:audit_dashboard"
  // Approval (legacy — kept for back-compat; not checked by new UI)
  | "approve:uploads"
  // Approval (granular — phase 1+)
  | "approve:expenses"
  | "approve:expenses_self"
  | "approve:expenses_high_value"
  | "approve:invoices"
  | "approve:invoices_self"
  | "approve:invoices_high_value"
  // Payment (phase 4+)
  | "initiate:payments"
  // Export / admin
  | "export:mine"
  | "export:company"
  | "manage:members"
  | "manage:org";

export type Role = "owner" | "admin" | "accountant" | "employee" | "auditor";

// Canonical list of every Capability value. Kept in sync with the union
// above; the type check below guarantees every member is present exactly
// once.
export const ALL_CAPABILITIES: ReadonlyArray<Capability> = [
  "view:mine",
  "view:company",
  "write:expenses_own",
  "write:expenses_any",
  "write:invoices_own",
  "write:invoices_any",
  "write:vendors",
  "write:tax_policies",
  "read:tax_policies",
  "read:reports",
  "read:audit_dashboard",
  "approve:uploads",
  "approve:expenses",
  "approve:expenses_self",
  "approve:expenses_high_value",
  "approve:invoices",
  "approve:invoices_self",
  "approve:invoices_high_value",
  "initiate:payments",
  "export:mine",
  "export:company",
  "manage:members",
  "manage:org",
] as const;

// Compile-time exhaustiveness guard: if ALL_CAPABILITIES drifts from the
// Capability union, _CapabilityExhaustiveness will fail to resolve to
// `never` and typecheck errors out.
type _AllCapsTuple = typeof ALL_CAPABILITIES[number];
type _CapabilityExhaustiveness =
  | Exclude<Capability, _AllCapsTuple>
  | Exclude<_AllCapsTuple, Capability>;
const _capabilityExhaustiveness: _CapabilityExhaustiveness extends never
  ? true
  : false = true;
void _capabilityExhaustiveness;

export const ROLE_DEFAULTS: Record<Role, ReadonlyArray<Capability>> = {
  // Owner has every capability, including self-approval and high-value
  // approval. Owners are the last line of defense against the app getting
  // stuck — never restrict them without explicit product direction.
  owner: ALL_CAPABILITIES,

  // Admin has every capability EXCEPT self-approval. Admins who need it
  // opt-in via the per-member override table (wave 19). This keeps the
  // default posture aligned with separation-of-duties norms without
  // blocking single-person orgs (where the owner self-approves).
  admin: ALL_CAPABILITIES.filter(
    (c) => c !== "approve:expenses_self" && c !== "approve:invoices_self",
  ),

  // Accountant: read-heavy; approves legacy "uploads" only. Expense/invoice
  // approval is not granted by default — if an org wants their accountant
  // to approve, flip it on via the permissions matrix (overrides). This
  // preserves today's behavior where accountants cannot approve through
  // the Review UI.
  accountant: [
    "view:company",
    "read:audit_dashboard",
    "read:reports",
    "read:tax_policies",
    "export:company",
    "approve:uploads",
    "write:expenses_own",
    "write:invoices_own",
    "export:mine",
  ],

  employee: [
    "view:mine",
    "write:expenses_own",
    "write:invoices_own",
    "read:reports",
    "read:tax_policies",
    "export:mine",
  ],

  auditor: ["read:audit_dashboard"],
};

export function defaultCapabilitiesFor(role: Role): ReadonlySet<Capability> {
  return new Set(ROLE_DEFAULTS[role]);
}
